import { PhoneInput } from "../misc/PhoneInput";
import { required, useRecordContext, useTranslate } from "ra-core";

import { TextInput } from "@/components/admin/text-input";

import { Separator } from "@/components/ui/separator";

import { useIsMobile } from "@/hooks/use-mobile";

import ImageEditorField from "../misc/ImageEditorField";

import type { Company } from "../types";

import { CompanyTagsInput } from "./CompanyTagsInput";

const isUrl = (url: string) => {
  if (!url) {
    return;
  }

  const UrlRegex = new RegExp(
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
  );

  if (!UrlRegex.test(url)) {
    return {
      message: "crm.validation.invalid_url",

      args: {
        _: "Введите корректный адрес сайта",
      },
    };
  }
};

export const CompanyInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-6 p-1">
      <CompanyDisplayInputs />

      <div className={`flex gap-8 ${isMobile ? "flex-col" : "flex-row"}`}>
        <div className="flex flex-1 flex-col gap-8">
          <CompanyContactInputs />

          <CompanyTagsInput />
        </div>

        <Separator orientation={isMobile ? "horizontal" : "vertical"} />

        <div className="flex flex-1 flex-col gap-8">
          <CompanyAddressInputs />

          <CompanyOperationalInputs />

          <CompanyDescriptionInputs />
        </div>
      </div>
    </div>
  );
};

const CompanyDisplayInputs = () => {
  const translate = useTranslate();

  const record = useRecordContext<Company>();

  return (
    <div className="flex flex-row gap-4">
      <ImageEditorField
        source="logo"
        type="avatar"
        width={60}
        height={60}
        emptyText={record?.name?.charAt(0)}
        linkPosition="bottom"
      />

      <TextInput
        source="name"
        className="w-full h-fit"
        validate={required()}
        helperText={false}
        placeholder={translate("resources.companies.fields.name", {
          _: "Название компании",
        })}
      />
    </div>
  );
};

const CompanyContactInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Контактные данные</h6>

      <PhoneInput source="phone_number" helperText={false} />

      <TextInput source="website" helperText={false} validate={isUrl} />
    </div>
  );
};

const CompanyAddressInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Адрес</h6>

      <TextInput source="address" helperText={false} />

      <div className="grid grid-cols-2 gap-3">
        <TextInput source="city" helperText={false} />

        <TextInput source="zipcode" helperText={false} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <TextInput source="state_abbr" helperText={false} />

        <TextInput source="country" helperText={false} />
      </div>
    </div>
  );
};

const CompanyOperationalInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h6 className="text-lg font-semibold">Рабочая информация</h6>

        <p className="mt-1 text-sm text-muted-foreground">
          Полезная информация для сотрудников
        </p>
      </div>

      <TextInput
        source="communication_hours"
        label="Часы связи"
        helperText={false}
        placeholder="Например: Пн–Пт, 09:00–18:00"
      />

      <TextInput
        source="average_lead_time_days"
        label="Средний срок, дней"
        type="number"
        helperText={false}
        placeholder="Например: 14"
      />
    </div>
  );
};

const CompanyDescriptionInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">Заметка</h6>

      <TextInput source="description" multiline helperText={false} />
    </div>
  );
};
